<template>
  <div id="collector">
    <!-- Collector -->
    <b-card
      id="collector"
      class="blockDrawer large"
      :class="$store.state.drawer.collector.position"
    >
      <template v-slot:header v-if="$store.state.display.editMode">
        <a
          class="close clickable"
          @click="closeEditCollector()"
        >
          <i class="far fa-times"></i>
        </a>

        <h4>Edit Data Point</h4>
      </template>
      <template v-slot:header v-else>
        <a
          class="close clickable"
          @click="closeCollector()"
        >
          <i class="far fa-times"></i>
        </a>

        <h4>{{ $store.state.collector.name }}</h4>
      </template>

      <p class="lead" v-if="$store.state.collector.description">{{ $store.state.collector.description }}</p>

      <div v-if="!$store.state.display.editMode">
        <p v-if="isPolygonCollector && !$store.state.collector.selectedGeometry" class="text-info"><small>Draw an area on the map using the polygon tool, then fill the form below and save.</small></p>
        <p v-if="isPolygonCollector && $store.state.collector.selectedGeometry" class="text-success"><small>Area drawn. Fill the form and click Save.</small></p>
        <p v-if="isLineCollector && !$store.state.collector.selectedGeometry" class="text-info"><small>Draw a line on the map using the line tool, then fill the form below and save.</small></p>
        <p v-if="isLineCollector && $store.state.collector.selectedGeometry" class="text-success"><small>Line drawn. Fill the form and click Save.</small></p>
        <template v-if="!isPolygonCollector && !isLineCollector">
          <p v-if="!$store.state.userLocation.accuracy"><small><span class="bad">No GPS Signal. Be sure to select location on the map!</span></small></p>
          <p v-if="$store.state.userLocation.accuracy > 5"><small>GPS Accuracy: {{ $store.state.userLocation.accuracy }}M (<span class="bad">Bad</span>)</small></p>
          <p v-if="$store.state.userLocation.accuracy <= 5 && $store.state.userLocation.accuracy > 3"><small>GPS Accuracy: {{ $store.state.userLocation.accuracy }}M (<span class="ok">Ok</span>)</small></p>
          <p v-if="$store.state.userLocation.accuracy <= 3"><small>GPS Accuracy: {{ $store.state.userLocation.accuracy }}M (<span class="good">Good</span>)</small></p>
        </template>
      </div><!-- is editmode -->
      <form @submit.prevent class="mb-5 pb-5">
        <b-alert
          v-if="$store.state.display.editMode && !isGeometryCollector"
          show
        >NOTE: You can update the location of this data point by clicking on the map.</b-alert>
        <b-alert
          v-if="$store.state.display.editMode && isPolygonCollector"
          show
          variant="info"
        >Adjust the polygon on the map using the draw tools, then click Save.</b-alert>
        <b-alert
          v-if="$store.state.display.editMode && isLineCollector"
          show
          variant="info"
        >Adjust the line on the map using the draw tools, then click Save.</b-alert>

        <div v-for="(groupedFields, group) in groupedBlockFields" v-bind:key="group">
          <h5 class="text-underline" v-if="group !== 'Others'">{{ group }}</h5> <!-- Group Title -->

          <!-- FIELDS -->
          <div
            v-for="(field, i) in groupedFields"
            v-bind:key="i"
            class="form-group mt-4 mb-4"
          >
            <div v-if="!field.hidden">
              <label><strong><span v-if="field.type === 'Computer Vision'"><i class="fas fa-robot mr-1"></i></span>{{ field.label }}</strong><span v-if="field.type == 'Number' && field.min && field.max && field.step"><strong>:</strong> {{ dataPoint[field.machine_name] }} <span v-if="field.suffix">{{ field.suffix }}</span></span></label>

              <div v-if="field.isLogEnabled" class="card p-2 mb-2">
                <p class="mb-2"><small><strong>Latest:</strong> {{ getLatestLogDisplay(field) }}{{ (field.type === 'Number' && field.suffix) ? ' ' + field.suffix : '' }}</small> <b-button v-if="dataPoint.id" size="sm" variant="outline-secondary" class="ml-2" @click="openLogHistory(field)">View logs</b-button></p>
                <label class="small mb-1">Date for this entry</label>
                <b-form-datepicker
                  v-model="getLogDraft(field.machine_name, field).logDate"
                  class="mb-2"
                  reset-button
                />
                <label class="small mb-1">Time for this entry</label>
                <b-form-timepicker
                  v-model="getLogDraft(field.machine_name, field).logTime"
                  locale="en"
                  class="mb-2"
                  reset-button
                />
                <template v-if="getLogValueType(field) === 'datetime'">
                  <b-form-datepicker
                    v-model="getLogDraft(field.machine_name, field).date"
                    class="mb-2"
                    reset-button
                    placeholder="Date"
                  />
                  <b-form-timepicker
                    v-if="field.includeTime"
                    v-model="getLogDraft(field.machine_name, field).time"
                    locale="en"
                    class="mb-2"
                    reset-button
                  />
                  <b-button size="sm" class="mr-1 mb-2" @click="setLogDraftToNow(field)">{{ field.includeTime ? 'Set to Now' : 'Set to Today' }}</b-button>
                </template>
                <input
                  v-else-if="getLogValueType(field) === 'text'"
                  v-model="getLogDraft(field.machine_name, field).value"
                  class="form-control mb-2"
                  placeholder="Add a new log entry"
                />
                <template v-else-if="getLogValueType(field) === 'number'">
                  <input
                    v-if="!field.min || !field.max || !field.step"
                    v-model="getLogDraft(field.machine_name, field).value"
                    class="form-control mb-2"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    placeholder="Add a new numeric entry"
                  />
                  <div v-else class="mb-2">
                    <b-form-input
                      v-model="getLogDraft(field.machine_name, field).value"
                      type="range"
                      :min="field.min"
                      :max="field.max"
                      :step="field.step"
                    />
                    <p class="mb-0 mt-1"><strong>{{ getLogDraft(field.machine_name, field).value }}</strong><span v-if="field.suffix"> {{ field.suffix }}</span></p>
                  </div>
                  <span v-if="field.suffix && (!field.min || !field.max || !field.step)" class="small text-muted">{{ field.suffix }}</span>
                </template>
                <b-form-select
                  v-else-if="getLogValueType(field) === 'boolean'"
                  v-model="getLogDraft(field.machine_name, field).value"
                  class="mb-2"
                  :options="[{ value: '', text: 'Select' }, { value: true, text: 'True' }, { value: false, text: 'False' }]"
                />
                <div v-else-if="getLogValueType(field) === 'select'" class="mb-2">
                  <span
                    v-for="(option, oi) in field.options"
                    :key="oi"
                    class="mr-1"
                  >
                    <b-btn
                      :variant="getLogDraft(field.machine_name, field).value === option ? 'success' : 'outline-secondary'"
                      size="sm"
                      @click="getLogDraft(field.machine_name, field).value = option"
                    >{{ option }}</b-btn>
                  </span>
                </div>
                <div v-else-if="getLogValueType(field) === 'image'" class="mb-2">
                  <p class="small text-muted mb-1">Add one or more photos for this log entry (e.g. updated shots from a revisit). Submit the form to save.</p>
                  <ImageUpload
                    :key="'log-img-' + field.machine_name + '-' + (getLogDraft(field.machine_name, field).value && getLogDraft(field.machine_name, field).value.length ? 'set' : 'empty')"
                    :mediaObject="getLogDraft(field.machine_name, field).value || []"
                    v-on:mediaDelivery="logImageDelivered($event, field)"
                  />
                </div>
                <input
                  v-model="getLogDraft(field.machine_name, field).notes"
                  class="form-control"
                  placeholder="Optional note"
                />
              </div>

              <input
                v-if="field.type == 'Text' && !field.isLogEnabled"
                v-model="dataPoint[field.machine_name]"
                class="form-control"
              />

              <input
                v-if="(field.type == 'CV Number' || field.type == 'Number' && !field.step) && !field.isLogEnabled"
                v-model="dataPoint[field.machine_name]"
                class="form-control"
                type="number"
              />

              <div
                v-if="field.type == 'Number' && field.min && field.max && field.step && !field.isLogEnabled"
              >
                <b-form-input
                  v-model="dataPoint[field.machine_name]"
                  type="range"
                  :min="field.min"
                  :max="field.max"
                  :step="field.step"
                ></b-form-input>

                <!-- <p class="text-center lead">{{ dataPoint[field.machine_name] }}</p> -->
              </div><!-- v-if -->

              <!-- <b-form-radio-group
                v-if="field.type == 'Select'"
                v-model="dataPoint[field.machine_name]"
                :options="field.options"
              ></b-form-radio-group> -->
              <!-- <b-form-select
                v-if="field.type == 'Select'"
                v-model="dataPoint[field.machine_name]"
                :options="field.options"
                size="lg"
              ></b-form-select> -->
              <div
                v-if="field.type == 'Select' && showSelects && !field.isLogEnabled"
              >
                <span
                  v-for="(option, i) in field.options"
                  v-bind:key="i"
                  class="mr-1"
                >
                  <b-btn
                    v-if="dataPoint[field.machine_name] === option"
                    @click="selectOption(field.machine_name, option)"
                    variant="success"
                  >{{ option }}</b-btn>
                  <b-btn
                    v-else
                    @click="selectOption(field.machine_name, option)"
                  >{{ option }}</b-btn>
                </span><!-- /OPTIONS -->
              </div><!-- /IF Select -->

              <!-- DATE AND TIMES -->
              <div
                v-if="field.type == 'Date and Time' && !field.isLogEnabled"
              >
                <b-form-datepicker
                  v-if="!hideTimeFields"
                  reset-button
                  @input="setDateTime(field.machine_name, 'date', $event)"
                  class="mb-2"
                  v-model="dataPoint[field.machine_name].date"
                ></b-form-datepicker>
                <b-form-timepicker
                  reset-button
                  v-if="field.includeTime && !hideTimeFields"
                  @input="setDateTime(field.machine_name, 'time', $event)"
                  locale="en"
                  v-model="dataPoint[field.machine_name].time"
                ></b-form-timepicker>
                <b-button
                  @click="setToNow(field.machine_name)"
                  class="mt-2"
                  v-if="field.includeTime"
                >Set to Now</b-button>
                <b-button
                  @click="setToNow(field.machine_name)"
                  class="mt-2"
                  v-else
                >Set to Today</b-button>
              </div><!-- /IF Date and Time -->

              <ImageUpload
                v-if="field.type == 'Image' && !field.isLogEnabled"
                :mediaObject="dataPoint[field.machine_name]"
                v-on:mediaDelivery="imagesDelivered($event, field)"
              />

              <CVImageUpload
                v-if="field.type == 'Computer Vision'"
                :mediaObject="dataPoint[field.machine_name]"
                :fieldProperties="field"
                v-on:mediaDelivery="CVImagesDelivered($event, field)"
              />
            </div><!-- /IF NOT HIDDEN-->
          </div><!-- /.form-group -->
        </div><!-- /GROUPS -->

        <!-- VERSION CONTROL -->
        <div
          v-if="$store.state.display.editMode"
          class="form-group mt-4 mb-4"
        >
          <label><strong>Revision Message</strong><br><small>Use this optional field to log what you've changed in this version of this datapoint. This helps track changes over time.</small></label>
          <input
            v-model="revisionMessage"
            class="form-control"
          />
        </div><!-- /.form-group -->

        <div v-if="$store.state.display.editMode" class="drawer-footer">
          <button
            @click="submitDatapoint()"
            class="btn btn-primary mr-1 w-50"
          >Save</button>
          <button
            @click="closeEditCollector()"
            class="btn btn-secondary mr-1"
          >Cancel</button>
          <button
            @click="confirmDelete = true"
            class="btn btn-danger"
            v-if="$store.state.role === 'Administrator'"
          >Delete</button>
        </div><!-- /.float-right -->
        <div v-else class="drawer-footer">
          <button
            @click="submitDatapoint()"
            class="btn btn-primary mr-1 w-50"
          >Submit</button>
          <button
            @click="closeCollector()"
            class="btn btn-secondary"
          >Cancel</button>
        </div><!-- /.float-right -->
      </form>
    </b-card>
    <Confirm
      v-if="confirmDelete"
      text="DANGER! Are you sure you want to delete this data point? THIS CANNOT BE UNDONE!!"
      v-on:confirmed="deleteDataPoint($event)"
    />
    <LogHistoryModal
      :show="showLogModal"
      entity-type="datapoint"
      :entity-id="dataPoint && dataPoint.id"
      :collector-id="collector && collector.id"
      :farm-id="(collector && collector.farmId) || ($store.state.farm && $store.state.farm.id)"
      :header="logHistoryHeader"
      :can-edit="$store.state.role === 'Administrator' || $store.state.role === 'Editor' || (collector && collector.editors && collector.editors.includes($store.state.currentUser && $store.state.currentUser.uid))"
      @updated="onLogUpdated"
      @close="showLogModal = false"
    />
  </div><!-- /#createMapDrawer -->
</template>

<script>
import Confirm from '../Confirm'
import LogHistoryModal from '../LogHistoryModal'
import firebase from 'firebase/compat/app'
import ImageUpload from './ImageUpload'
import CVImageUpload from './CVImageUpload'
import { appendDatapointLogEntry } from '../../services/HeaderLogService'
const fb = require('../../firebaseConfig.js')
const turf = require('@turf/turf')

export default {
  data () {
    return {
      confirmDelete: false,
      revisionMessage: '',
      showSelects: true,
      hideTimeFields: false,
      logDrafts: {},
      showLogModal: false,
      logHistoryHeader: null
    }
  },
  components: {
    Confirm,
    LogHistoryModal,
    ImageUpload,
    CVImageUpload
  },
  computed: {
    dataPoint: {
      get () {
        return this.$store.state.dataPoint
      },
      set (value) {
        this.$store.commit('setDataPoint', value)
      }
    },
    collector: {
      get () {
        return this.$store.state.collector
      },
      set (value) {
        this.$store.commit('setCollector', value)
      }
    },
    groupedBlockFields () {
      return this.$store.state.collector.fields.reduce((grouped, field) => {
        const group = field.group || 'Others'
        if (!grouped[group]) {
          grouped[group] = []
        }
        grouped[group].push(field)
        return grouped
      }, {})
    },
    isPolygonCollector () {
      return (this.$store.state.collector && this.$store.state.collector.geometryType) === 'polygon'
    },
    isLineCollector () {
      return (this.$store.state.collector && this.$store.state.collector.geometryType) === 'line'
    },
    isGeometryCollector () {
      return this.isPolygonCollector || this.isLineCollector
    }
  },
  created () {
    console.log('Created Collector Form')
    this.$store.commit('setCollecting', true)
    // Formatting function for date
    function formatDate (dateObject) {
      const dd = String(dateObject.getDate()).padStart(2, '0')
      const mm = String(dateObject.getMonth() + 1).padStart(2, '0') // January is 0
      const yyyy = dateObject.getFullYear()

      return yyyy + '-' + mm + '-' + dd
    }

    // Formatting function for time
    function formatTime (dateObject) {
      const hh = String(dateObject.getHours()).padStart(2, '0')
      const mm = String(dateObject.getMinutes()).padStart(2, '0')
      const ss = String(dateObject.getSeconds()).padStart(2, '0')

      return hh + ':' + mm + ':' + ss
    }
    // MAKE SURE ANY DATE AND TIME ARE FORMETTED
    this.collector.fields.forEach(field => {
      if (field.type === 'Date and Time') {
        if (this.dataPoint[field.machine_name]) {
          // CONVERT BACK TO OBJECT
          // Convert the Firestore timestamp to a JavaScript Date object.
          const dateObject = this.dataPoint[field.machine_name].toDate()

          // Now create the final object
          this.dataPoint[field.machine_name] = {
            date: formatDate(dateObject),
            time: formatTime(dateObject)
          }
        } else {
          // this.setToNow(field.machine_name)
          this.dataPoint[field.machine_name] = {
            date: '',
            time: ''
          }
        }
      }
    })
    if (!this.$store.state.display.editMode) {
      // this.collector.fields.forEach(field => {
      //   if (field.type === 'Multiselect') {
      //     this.dataPoint[field.machine_name] = []
      //   }
      // })
    } else {
      const payload = { ...this.collector }
      if (this.isGeometryCollector && this.dataPoint.geometry) {
        payload.editGeometry = this.dataPoint.geometry
      }
      this.$store.dispatch('collect', payload)
    }
    this.$store.commit('setStateProperty', { property: 'pauseMap', value: true })
    // this.showSelects = true
  },
  methods: {
    getCollectorGeometryType () {
      return this.$store.state.collector && this.$store.state.collector.geometryType
    },
    parseCollectorGeometry (geometryInput) {
      if (!geometryInput) return null
      try {
        const geom = typeof geometryInput === 'string' ? JSON.parse(geometryInput) : geometryInput
        const expectedType = this.getCollectorGeometryType()
        if (expectedType === 'polygon' && (geom.type === 'Polygon' || geom.type === 'MultiPolygon')) return geom
        if (expectedType === 'line' && (geom.type === 'LineString' || geom.type === 'MultiLineString')) return geom
      } catch (e) { /* ignore */ }
      return null
    },
    getRepresentativeGeoPointForGeometry (geometryInput) {
      const geom = this.parseCollectorGeometry(geometryInput)
      if (!geom) return null
      try {
        const feature = { type: 'Feature', properties: {}, geometry: geom }
        let representativePoint = turf.centroid(feature)
        if (this.getCollectorGeometryType() === 'line') {
          const lengthKm = turf.length(feature, { units: 'kilometers' })
          representativePoint = turf.along(feature, lengthKm / 2, { units: 'kilometers' })
        }
        const lat = representativePoint.geometry.coordinates[1]
        const lng = representativePoint.geometry.coordinates[0]
        return new firebase.firestore.GeoPoint(lat, lng)
      } catch (e) {
        return null
      }
    },
    getLogDraft (machineName, field) {
      if (!this.logDrafts[machineName]) {
        const d = new Date()
        const logDate = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
        const logTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0')
        const base = { notes: '', logDate, logTime }
        const type = field && (field.logValueType || this.deriveLogValueType(field.type))
        if (type === 'datetime') {
          this.$set(this.logDrafts, machineName, { ...base, date: '', time: field && field.includeTime ? '' : '00:00:00' })
        } else if (type === 'image') {
          this.$set(this.logDrafts, machineName, { ...base, value: [] })
        } else {
          this.$set(this.logDrafts, machineName, { ...base, value: '' })
        }
      }
      return this.logDrafts[machineName]
    },
    deriveLogValueType (headerType) {
      const t = (headerType || '').toLowerCase()
      if (t === 'number') return 'number'
      if (t === 'text') return 'text'
      if (t === 'select') return 'select'
      if (t === 'date and time') return 'datetime'
      if (t === 'image') return 'image'
      return 'text'
    },
    getLogValueType (field) {
      return field.logValueType || this.deriveLogValueType(field.type)
    },
    getLatestLogValue (field) {
      if (!field || !field.machine_name) return null
      const latest = this.dataPoint.latestLogs && this.dataPoint.latestLogs[field.machine_name]
      if (!latest || latest.value === undefined || latest.value === null) return null
      return latest.value
    },
    getLatestLogDisplay (field) {
      const raw = this.getLatestLogValue(field)
      if (raw === null || raw === undefined) return '—'
      if (this.getLogValueType(field) === 'datetime' && raw && typeof raw.toDate === 'function') {
        const d = raw.toDate()
        return field.includeTime ? d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : d.toLocaleDateString(undefined, { dateStyle: 'short' })
      }
      if (this.getLogValueType(field) === 'image' && raw) {
        if (Array.isArray(raw)) {
          const count = raw.filter(img => img && typeof img === 'object' && (img.path || img.url || img.small || img.large)).length
          if (!count) return '—'
          return count === 1 ? '1 image' : `${count} images`
        }
        if (typeof raw === 'object') return '1 image'
      }
      if (raw === '') return '—'
      return raw
    },
    setLogDraftToNow (field) {
      const draft = this.getLogDraft(field.machine_name, field)
      const now = new Date()
      draft.date = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0')
      draft.time = String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0') + ':' + String(now.getSeconds()).padStart(2, '0')
    },
    logImageDelivered (payload, field) {
      const draft = this.getLogDraft(field.machine_name, field)
      draft.value = Array.isArray(payload) ? payload : []
    },
    getPendingLogEntries () {
      const entries = []
      this.collector.fields.forEach(field => {
        if (!field.isLogEnabled) return
        const draft = this.getLogDraft(field.machine_name, field)
        const logDate = draft.logDate || (() => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') })()
        const type = this.getLogValueType(field)
        let normalizedValue
        if (type === 'datetime') {
          if (!draft.date) return
          const time = (draft.time || '00:00:00').substring(0, 8)
          const date = new Date(draft.date + 'T' + time)
          normalizedValue = firebase.firestore.Timestamp.fromDate(date)
        } else if (type === 'image') {
          if (!Array.isArray(draft.value) || !draft.value.length) return
          normalizedValue = draft.value
        } else {
          if (draft.value === '' || draft.value === undefined || draft.value === null) return
          normalizedValue = draft.value
          if (type === 'number') {
            normalizedValue = Number(draft.value)
            if (isNaN(normalizedValue)) return
          }
        }
        entries.push({
          header: field,
          value: normalizedValue,
          notes: draft.notes || '',
          logDate,
          logTime: draft.logTime
        })
      })
      return entries
    },
    getLogSelectOptions (field) {
      const options = [{ value: '', text: 'Select' }]
      ;(field.options || []).forEach(option => {
        options.push({ value: option, text: option })
      })
      return options
    },
    openLogHistory (field) {
      this.logHistoryHeader = field
      this.showLogModal = true
    },
    async onLogUpdated () {
      if (this.dataPoint && this.dataPoint.id && this.collector && this.collector.id) {
        const doc = await fb.collectorsCollection.doc(this.collector.id).collection('dataPoints').doc(this.dataPoint.id).get()
        if (doc.exists) {
          const dataPointData = { ...doc.data(), id: doc.id }
          this.$store.commit('setDataPoint', dataPointData)
          this.$store.dispatch('refreshDatasetGeoJsonFromDatapoint', { dataPointId: doc.id, dataPointData })
        }
        await fb.collectorsCollection.doc(this.collector.id).update({ reCompile: true })
      }
    },
    appendPendingDatapointLogs (dataPointId) {
      const entries = this.getPendingLogEntries()
      if (!entries.length) return Promise.resolve()
      return Promise.all(entries.map(entry => {
        const timeStr = (entry.logTime || '12:00:00').substring(0, 8)
        const timestamp = entry.logDate ? new Date(entry.logDate + 'T' + timeStr) : new Date()
        return appendDatapointLogEntry({
          collectorId: this.collector.id,
          dataPointId,
          farmId: this.collector.farmId || this.$store.state.farm.id || '',
          header: entry.header,
          value: entry.value,
          notes: entry.notes,
          userId: fb.auth.currentUser && fb.auth.currentUser.uid,
          timestamp
        }).then(() => {
          this.dataPoint[entry.header.machine_name] = entry.value
          if (!this.dataPoint.latestLogs) {
            this.$set(this.dataPoint, 'latestLogs', {})
          }
          this.$set(this.dataPoint.latestLogs, entry.header.machine_name, {
            value: entry.value,
            timestamp: firebase.firestore.Timestamp.now(),
            notes: entry.notes,
            userId: fb.auth.currentUser && fb.auth.currentUser.uid
          })
          const draft = this.getLogDraft(entry.header.machine_name, entry.header)
        const d = new Date()
        const logDate = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
        const logTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0')
        draft.notes = ''
        draft.logDate = logDate
        draft.logTime = logTime
          if (this.getLogValueType(entry.header) === 'datetime') {
            draft.date = ''
            draft.time = entry.header.includeTime ? '' : '00:00:00'
          } else if (this.getLogValueType(entry.header) === 'image') {
            draft.value = []
          } else {
            draft.value = ''
          }
        })
      }))
    },
    setToNow (machineName) {
      this.hideTimeFields = true
      function getToday () {
        let today = new Date()
        const dd = String(today.getDate()).padStart(2, '0')
        const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
        const yyyy = today.getFullYear()

        today = yyyy + '-' + mm + '-' + dd
        return today
      }
      function getCurrentTime () {
        const now = new Date()
        const hh = String(now.getHours()).padStart(2, '0')
        const mm = String(now.getMinutes()).padStart(2, '0')
        const ss = String(now.getSeconds()).padStart(2, '0')

        const time = hh + ':' + mm + ':' + ss
        return time
      }
      this.dataPoint[machineName] = {
        date: getToday(),
        time: getCurrentTime()
      }
      setTimeout(() => {
        this.hideTimeFields = false
      }, 1)
    },
    setDateTime (machineName, option, event) {
      function getToday () {
        let today = new Date()
        const dd = String(today.getDate()).padStart(2, '0')
        const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
        const yyyy = today.getFullYear()

        today = yyyy + '-' + mm + '-' + dd
        return today
      }
      if (!this.dataPoint[machineName]) {
        this.dataPoint[machineName] = {
          date: false,
          time: false
        }
      }
      if (option === 'date') {
        this.dataPoint[machineName].date = event
      }
      if (option === 'time') {
        this.dataPoint[machineName].time = event
        if (!this.dataPoint[machineName].date) {
          this.dataPoint[machineName].date = getToday()
        }
      }
    },
    selectOption (field, option) {
      this.showSelects = false
      this.dataPoint[field] = option
      this.showSelects = true
    },
    imagesDelivered (e, field) {
      this.dataPoint[field.machine_name] = e
    },
    CVImagesDelivered (e, field) {
      this.dataPoint[field.machine_name] = e
      console.log(e)
      console.log(field)
      // field.options.forEach(option => {
      //   if (option === 'Count Clusters') {
      //     console.log(this.dataPoint)
      //     // this.dataPoint.cv_cluster_count = e[0].cv.predictions.length
      //     // this.$set(this.dataPoint, 'cv_cluster_count', e[0].cv.predictions.length)
      //   }
      // })
    },
    deleteDataPoint (confirmed) {
      if (confirmed === true) {
        this.$store.dispatch('turnOffCollectorClick')
        this.$store.commit('tuckDrawer', 'collector')
        this.confirmDelete = false
        this.$store.commit('setEditMode', false)
        // this.collector.fields.forEach((field, i) => {
        //   if (field.type === 'Image') {
        //     this.dataPoint[field.machine_name].forEach((imageItem, i) => {
        //       console.log(imageItem)
        //       firebase.storage().ref(imageItem.path).delete()
        //       firebase.storage().ref(imageItem.small).delete()
        //       firebase.storage().ref(imageItem.medium).delete()
        //       firebase.storage().ref(imageItem.large).delete()
        //     })
        //   }
        // })
        fb.collectorsCollection.doc(this.collector.id).collection('dataPoints').doc(this.dataPoint.id).delete().then(() => {
          this.$store.commit('setAlert', { active: true, message: 'Data Point Deleted! Recompiling map now...', level: 'alert-success' })
          // this.$store.commit('setDrawer', { name: 'collector', position: 'out' })
          return this.$store.dispatch('removeAllLayers')
        }).then(() => {
          return fb.collectorsCollection.doc(this.collector.id).update({
            updatedOn: new Date(),
            reCompile: true
          })
        }).then(() => {
          this.$store.dispatch('launchDataset', { dataset: this.$store.state.dataset, collecting: false, reCompile: true })
          // const dataset = this.$store.state.dataset
          // return this.$store.dispatch('mapDataset', dataset)
        }).then(() => {
          // let newData = true
          // const self = this
          // fb.datasetsCollection.doc(self.$store.state.dataset.id).onSnapshot(snapshot => {
          //   const dataset = self.$store.state.dataset
          //   const datasetDoc = snapshot.data()
          //   datasetDoc.id = snapshot.id
          //   const filePath = dataset.file.geoJsonPath || ''
          //   const newFilePath = datasetDoc.file.geoJsonPath
          //   // Only rerender if new geoJson file
          //   if (filePath !== newFilePath && newData === true) {
          //     newData = false
          //     self.$store.commit('setDataset', datasetDoc)
          //     setTimeout(() => {
          //       self.$store.commit('setAlert', { active: false })
          //       self.$store.dispatch('removeAllLayers')
          //       // self.$store.dispatch('mapDataset', datasetDoc)
          //       self.$store.dispatch('launchDataset', { dataset: datasetDoc, collecting: true })
          //       console.log('New data file... mapping')
          //     }, 1000)
          //   }
          //   this.$store.commit('hideDrawer', 'collector')
          // })
          this.$store.commit('hideDrawer', 'collector')
        })
      } else {
        this.confirmDelete = false
        return false
      }
    },
    submitDatapoint () {
      let formComplete = true
      // SPECIAL FIELD FORMATTINGS
      this.collector.fields.forEach((field, i) => {
        let badNumber = false
        if (field.isLogEnabled) {
          const draft = this.getLogDraft(field.machine_name, field)
          const hasExistingLatest = this.getLatestLogValue(field) != null
          const type = this.getLogValueType(field)
          if (type === 'number' && draft.value !== '' && draft.value !== null && draft.value !== undefined) {
            if (isNaN(Number(draft.value))) {
              badNumber = true
            } else {
              draft.value = Number(draft.value)
            }
          }
          let logFilled = false
          if (type === 'datetime') logFilled = !!draft.date
          else if (type === 'image') logFilled = Array.isArray(draft.value) && draft.value.length > 0
          else logFilled = draft.value !== '' && draft.value !== undefined && draft.value !== null && !badNumber
          if (field.required && !hasExistingLatest && !logFilled) {
            this.$store.commit('setAlert', { active: true, message: 'Required field missing: ' + field.label, level: 'alert-warning', timeout: 3000 })
            formComplete = false
          }
          return
        }
        if (field.type === 'Number') {
          if (isNaN(this.dataPoint[field.machine_name])) {
            badNumber = true
          } else {
            const oldVal = this.dataPoint[field.machine_name]
            const newVal = Number(oldVal)
            this.dataPoint[field.machine_name] = newVal
          }
        }

        // DEAL WITH DATES AND TIMES
        if (field.type === 'Date and Time') {
          if (!this.dataPoint[field.machine_name]) {
            this.dataPoint[field.machine_name] = {}
          }
          if (this.dataPoint[field.machine_name].date) {
            if (!this.dataPoint[field.machine_name].time) {
              this.dataPoint[field.machine_name].time = '00:00:00'
            }
            const dateString = `${this.dataPoint[field.machine_name].date}T${this.dataPoint[field.machine_name].time}`
            const date = new Date(dateString)
            this.dataPoint[field.machine_name] = firebase.firestore.Timestamp.fromDate(date)
          }
        }
        // END DATES AND TIMES
        if (field.required) {
          if (this.dataPoint[field.machine_name] === undefined || this.dataPoint[field.machine_name].length < 1 || badNumber) {
            // alert('Required field missing: ' + field.label)
            this.$store.commit('setAlert', { active: true, message: 'Required field missing: ' + field.label, level: 'alert-warning', timeout: 3000 })
            formComplete = false
          }
        }
      })
      if (this.$store.state.display.editMode) {
        this.dataPoint.updatedOn = new Date()
        this.dataPoint.updatedBy = fb.auth.currentUser.uid
        this.dataPoint.updatedByName = this.$store.state.userProfile.firstName + ' ' + this.$store.state.userProfile.lastName || this.$store.state.userProfile.email
        if (this.isGeometryCollector) {
          const drawControl = this.$store.state.collector.collectorDrawControl
          if (drawControl) {
            const all = drawControl.getAll()
            if (all && all.features && all.features.length > 0 && all.features[0].geometry) {
              const geom = all.features[0].geometry
              const parsedGeometry = this.parseCollectorGeometry(geom)
              if (parsedGeometry) {
                this.dataPoint.geometry = JSON.stringify(parsedGeometry)
                const representativeLocation = this.getRepresentativeGeoPointForGeometry(parsedGeometry)
                if (representativeLocation) this.dataPoint.geolocation = representativeLocation
              }
            }
          }
        } else if (this.$store.state.collector.selectedLocation) {
          this.dataPoint.geolocation = this.$store.state.collector.selectedLocation
        }
        if (formComplete) {
          this.$store.commit('tuckDrawer', 'collector')
          this.$store.commit('setMapLoader', true)
          // this.$store.commit('setCollecting', false)
          this.$store.dispatch('turnOffCollectorClick')
          // SAVE REVISION FROM CURRENT DATA (REFETCH FIRST)
          fb.collectorsCollection.doc(this.collector.id).collection('dataPoints').doc(this.dataPoint.id).get().then(dataPointDoc => {
            const dataPointData = dataPointDoc.data()
            
            // Create a clean copy for revision (without id field to avoid conflicts)
            const revisionData = { ...dataPointData }
            delete revisionData.id
            
            return fb.collectorsCollection.doc(this.collector.id).collection('dataPoints').doc(this.dataPoint.id).collection('revisions').add(revisionData)
          }).then(() => {
            this.dataPoint.revisionMessage = this.revisionMessage
            return fb.collectorsCollection.doc(this.collector.id).collection('dataPoints').doc(this.dataPoint.id).update(this.dataPoint)
          }).then(() => {
            return this.appendPendingDatapointLogs(this.dataPoint.id)
          }).then(() => {
            // Mark the collector with a new updatedOn date. Since editing something old, recompile as well
            return fb.collectorsCollection.doc(this.collector.id).update({
              updatedOn: new Date(),
              reCompile: true
            })
          }).then(() => {
            this.$store.commit('setAlert', { active: true, message: 'Data Point Updated! Recompiling map now...', level: 'alert-success' })
            // this.$store.commit('setDrawer', { name: 'collector', position: 'out' })
            this.$store.commit('tuckDrawer', 'collector')
            return this.$store.dispatch('removeAllLayers')
          }).then(() => {
            // setTimeout(() => {
            // const datasetDoc = await fb.datasetsCollection.doc(this.$store.state.dataset.id).get()
            // const dataset = datasetDoc.data()
            // dataset.id = datasetDoc.id
            // this.$store.dispatch('turnOffCollectorClick')
            this.$store.commit('setEditMode', false)
            this.$store.commit('hideDrawer', 'collector')
            return this.$store.dispatch('launchDataset', { dataset: this.$store.state.dataset, collecting: false })
            // }, 4000)
          }).catch(err => {
            console.log(err)
          })
        }
      } else {
        // Create New Datapoint
        this.dataPoint.createdOn = new Date()
        this.dataPoint.updatedOn = new Date()
        this.dataPoint.createdBy = fb.auth.currentUser.uid
        this.dataPoint.updatedBy = fb.auth.currentUser.uid
        this.dataPoint.updatedByName = this.$store.state.userProfile.firstName + ' ' + this.$store.state.userProfile.lastName || this.$store.state.userProfile.email
        this.dataPoint.revisionMessage = 'Initial datapoint created.'
        if (this.isGeometryCollector && this.$store.state.collector.selectedGeometry) {
          const geom = this.$store.state.collector.selectedGeometry
          const parsedGeometry = this.parseCollectorGeometry(geom)
          if (parsedGeometry) {
            this.dataPoint.geometry = JSON.stringify(parsedGeometry)
            this.dataPoint.geolocation = this.$store.state.collector.selectedLocation || this.getRepresentativeGeoPointForGeometry(parsedGeometry)
          }
        } else if (this.$store.state.collector.selectedLocation) {
          this.dataPoint.geolocation = this.$store.state.collector.selectedLocation
        } else if (this.$store.state.userLocation.longitude && this.$store.state.userLocation.latitude) {
          this.dataPoint.geolocation = new firebase.firestore.GeoPoint(this.$store.state.userLocation.latitude, this.$store.state.userLocation.longitude)
          this.dataPoint.accuracy = this.$store.state.userLocation.accuracy
        } else if (!this.isGeometryCollector && !this.$store.state.userLocation.latitude) {
          this.$store.commit('setAlert', { active: true, message: 'Could not get user location! Please try again.', level: 'alert-warning', timeout: 3000 })
          return false
        } else if (this.isGeometryCollector && !this.$store.state.collector.selectedGeometry) {
          // Fallback: read geometry from draw control if event didn't update store
          const map = this.$store.state.mapObject
          const drawControl = this.$store.state.collector.collectorDrawControl || (map && map._collectorDrawControlRef)
          if (drawControl) {
            const all = drawControl.getAll()
            const first = all.features && all.features[0]
            if (first && first.geometry) {
              const geom = this.parseCollectorGeometry(first.geometry)
              const loc = this.getRepresentativeGeoPointForGeometry(geom)
              if (geom && loc) {
                this.$store.commit('setStateProperty', {
                  property: 'collector',
                  value: {
                    ...this.$store.state.collector,
                    selectedGeometry: geom,
                    selectedLocation: loc
                  }
                })
                this.dataPoint.geometry = JSON.stringify(geom)
                this.dataPoint.geolocation = loc
              }
            }
          }
          if (!this.$store.state.collector.selectedGeometry && !this.dataPoint.geometry) {
            const drawMessage = this.isLineCollector
              ? 'Draw a line on the map first, then fill the form and save.'
              : 'Draw an area on the map first, then fill the form and save.'
            this.$store.commit('setAlert', { active: true, message: drawMessage, level: 'alert-warning', timeout: 3000 })
            return false
          }
        }
        // CONVERT NUMBER FIELDS FROM STRINGS TO NUMBERS
        // this.$store.state.collector.fields.forEach((field, i) => {
        //   let badNumber = false
        //   if (field.type === 'Number') {
        //     if (isNaN(this.dataPoint[field.machine_name])) {
        //       badNumber = true
        //     } else {
        //       const oldVal = this.dataPoint[field.machine_name]
        //       const newVal = Number(oldVal)
        //       this.dataPoint[field.machine_name] = newVal
        //     }
        //   }
        //   if (field.required) {
        //     if (this.dataPoint[field.machine_name] === undefined || this.dataPoint[field.machine_name].length < 1 || badNumber) {
        //       formComplete = false
        //       // alert('Required field missing: ' + field.label)
        //       this.$store.commit('setAlert', { active: true, message: 'Required field missing: ' + field.label, level: 'alert-warning', timeout: 3000 })
        //     }
        //   }
        // })
        if (formComplete) {
          this.$store.commit('tuckDrawer', 'collector')
          // this.$store.commit('setCollecting', false)
          this.$store.dispatch('turnOffCollectorClick')
          // this.closeCollector()
          fb.collectorsCollection.doc(this.$store.state.collector.id).collection('dataPoints').add(this.dataPoint).then((docRef) => {
            const dataPointId = docRef.id
            // reCompile:true (not just updatedOn) — the "new data available"
            // check compares collector.updatedOn vs dataset.updatedOn, and any
            // later touch of the dataset doc (a settings save back at the
            // office) out-stamps the collector, so freshly collected points
            // never compile into the rendered GeoJSON and the collector looks
            // empty. Edits and deletes already set reCompile; adds must too.
            return fb.collectorsCollection.doc(this.$store.state.collector.id).update({
              updatedOn: new Date(),
              reCompile: true
            }).then(() => this.appendPendingDatapointLogs(dataPointId)).then(() => dataPointId)
          }).then((dataPointId) => {
            const drawControl = this.$store.state.collector.collectorDrawControl
            if (drawControl) {
              try { drawControl.deleteAll() } catch (e) { /* ignore */ }
            }
            this.$store.commit('setStateProperty', {
              property: 'collector',
              value: { ...this.$store.state.collector, selectedGeometry: null }
            })
            this.$store.commit('hideDrawer', 'collector')
            setTimeout(() => {
              this.$store.dispatch('collect', this.$store.state.collector)
            }, 1000)
            this.$store.commit('setAlert', { active: true, message: 'Data collected!', level: 'alert-success', timeout: 2000 })
            if (this.$store.state.dataset.dynamic) {
              this.$store.dispatch('appendDynamicDatapointToMap', {
                dataset: this.$store.state.dataset,
                dataPoint: { ...this.dataPoint },
                dataPointId,
                collector: this.$store.state.collector
              })
            }
            return fb.datasetsCollection.doc(this.collector.datasetId).get()
          }).then(doc => {
            const dataset = doc.data()
            dataset.id = doc.id
            this.$store.commit('setDataset', dataset)
            this.$store.commit('hideDrawer', 'collector')
          }).catch(err => {
            console.log(err)
          })
        }
      }
    },
    closeEditCollector () {
      this.$store.commit('setEditMode', false)
      if (this.$store.state.mapObject.getLayer('selectedDataPoint')) {
        this.$store.state.mapObject.removeLayer('selectedDataPoint')
        this.$store.state.mapObject.removeSource('selectedDataPoint')
      }
      if (this.$store.state.mapObject.getLayer('selectedDataPointHighlight')) {
        this.$store.state.mapObject.removeLayer('selectedDataPointHighlight')
      }
      if (this.$store.state.mapObject.getLayer('selectedDataPointPolygon')) {
        this.$store.state.mapObject.removeLayer('selectedDataPointPolygon')
      }
      this.$store.commit('setStateProperty', { property: 'pauseMap', value: false })
      this.$store.dispatch('turnOffCollectorClick')
      this.$store.commit('hideDrawer', 'collector')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'dataPointDetails')
      }, 300)
    },
    closeCollector () {
      this.collector.fields.forEach((field, i) => {
        if (field.type === 'Image') {
          if (this.dataPoint[field.machine_name]) {
            this.dataPoint[field.machine_name].forEach((imageItem, i) => {
              firebase.storage().ref(imageItem.path).delete()
              firebase.storage().ref(imageItem.small).delete()
              firebase.storage().ref(imageItem.medium).delete()
              firebase.storage().ref(imageItem.large).delete()
            })
          }
        }
      })
      this.collector.collecting = false
      // if (this.collector.selectMarker != null) {
      //   this.collector.selectMarker.remove()
      // }
      this.$store.dispatch('turnOffCollectorClick')
      this.$store.commit('hideDrawer', 'collector')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'collectors')
      }, 300)
    }
  }
}
</script>

<style scoped>
@media(max-width: 800px) {
  form {
    margin-bottom: 500px;
  }
}

.bad {
  color: red;
}

.ok {
  color: orange;
}

.good {
  color: green;
}
</style>
